'use client';

import { useAuth0 } from '@auth0/auth0-react';
import { Auth0Client } from '@auth0/auth0-spa-js';
import { useEffect, useRef } from 'react';

import { isLastActiveExpired } from '@libs/core';

import { logSentryError } from '../../utils';
import { getAuth0SpaClientOptions } from './auth0ClientOptions';
import { auth0TokenSyncSentryTags } from './auth0TokenSyncSentryTags';

const SILENT_SSO_PROBE_SENTRY = {
    component: 'useSilentSsoProbe',
    action: 'getAccessTokenSilently',
} as const;

// Expected when this app has no refresh token and the tenant has no session.
// missing_refresh_token is the same miss when the iframe fallback cannot run.
const SILENT_AUTH_MISS_ERROR_CODES = new Set(['login_required', 'missing_refresh_token']);

// The SPA SDK iframe-fallbacks these when useRefreshTokensFallback is on. Leave
// them to handleAccessTokenSyncError instead of minting a new SSO grant.
const REVOKED_REFRESH_TOKEN_ERROR_CODES = new Set(['invalid_grant', 'invalid_refresh_token']);

type SilentSsoProbeResult = 'restored' | 'miss';

type GetSilentTokenInput = {
    useRefreshTokensFallback?: boolean;
};

function getAuth0ErrorCode(error: unknown): string | undefined {
    if (!error || typeof error !== 'object' || !('error' in error) || typeof error.error !== 'string') {
        return undefined;
    }

    return error.error;
}

function isSilentAuthMiss(error: unknown): boolean {
    const errorCode = getAuth0ErrorCode(error);
    return Boolean(errorCode && SILENT_AUTH_MISS_ERROR_CODES.has(errorCode));
}

function isMissingRefreshToken(error: unknown): boolean {
    return getAuth0ErrorCode(error) === 'missing_refresh_token';
}

function isRevokedRefreshToken(error: unknown): boolean {
    const errorCode = getAuth0ErrorCode(error);
    return Boolean(errorCode && REVOKED_REFRESH_TOKEN_ERROR_CODES.has(errorCode));
}

function isExpectedSilentSsoFailure(error: unknown): boolean {
    return isSilentAuthMiss(error) || isRevokedRefreshToken(error);
}

function reportSilentSsoProbeError(error: unknown) {
    const reportedError = error instanceof Error ? error : new Error('Silent Auth0 session restore failed');
    logSentryError(reportedError, auth0TokenSyncSentryTags(SILENT_SSO_PROBE_SENTRY));
}

async function getSilentToken({ useRefreshTokensFallback = false }: GetSilentTokenInput = {}) {
    const probeClient = new Auth0Client({
        ...getAuth0SpaClientOptions(),
        ...(useRefreshTokensFallback ? { useRefreshTokensFallback: true } : {}),
    });

    await probeClient.getTokenSilently({ cacheMode: 'off' });
}

async function restoreSilentSsoWithIframe(): Promise<SilentSsoProbeResult> {
    try {
        await getSilentToken({ useRefreshTokensFallback: true });
        return 'restored';
    } catch (ssoError) {
        if (isExpectedSilentSsoFailure(ssoError)) {
            return 'miss';
        }

        throw ssoError;
    }
}

async function restoreSilentSsoSession(): Promise<SilentSsoProbeResult> {
    try {
        await getSilentToken();
        return 'restored';
    } catch (probeError) {
        if (isMissingRefreshToken(probeError)) {
            return restoreSilentSsoWithIframe();
        }

        if (isExpectedSilentSsoFailure(probeError)) {
            return 'miss';
        }

        throw probeError;
    }
}

/**
 * Restores an Auth0 session without Universal Login.
 *
 * `Auth0Provider` `checkSession` returns immediately unless
 * `auth0.<clientId>.is.authenticated` is already set, so a visit after sign-in
 * on another app in the same tenant never asks Auth0. This probe first uses its
 * own `Auth0Client` without iframe fallback. A missing refresh token is retried
 * with `useRefreshTokensFallback` so SSO can complete through a hidden
 * `prompt=none` iframe. The SDK also uses that iframe after `invalid_grant`, so
 * the first client and the provider leave fallback off. A revoked refresh token
 * is then left for `handleAccessTokenSyncError` instead of being replaced by SSO.
 *
 * On success the shared `localstorage` cache holds the new tokens. The provider
 * `getAccessTokenSilently()` reads that cache and sets `isAuthenticated`.
 * `useAuth0AccessTokenSync` then writes the access-token cookie. This hook does
 * not write cookies or redirect to login. `login_required` means there is no session.
 *
 * Skipped when `isLastActiveExpired()` is true. Logout and inactivity set that
 * flag before the SDK reports signed out, so a silent probe cannot undo them.
 */
export function useSilentSsoProbe() {
    const { error, getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();
    const probeRequestedRef = useRef(false);

    useEffect(() => {
        if (isLoading || isAuthenticated || error || isLastActiveExpired() || probeRequestedRef.current) {
            return;
        }

        probeRequestedRef.current = true;

        void (async () => {
            try {
                const result = await restoreSilentSsoSession();
                if (result === 'miss') {
                    return;
                }
            } catch (probeError) {
                reportSilentSsoProbeError(probeError);
                return;
            }

            // Read the shared localStorage cache through the provider client so
            // isAuthenticated updates. That client still has iframe fallback off.
            try {
                await getAccessTokenSilently();
            } catch (adoptError) {
                reportSilentSsoProbeError(adoptError);
            }
        })();
    }, [error, getAccessTokenSilently, isAuthenticated, isLoading]);
}
